'use es6';

import SeamlessImmutable from 'seamless-immutable';
import { result } from 'underscore';

const tryGetConstructorName = (ctor, defaultName = null) => {
  return ctor && typeof ctor.name === 'string' ? ctor.name : defaultName;
};
/**
 * Creates an immutable object from a given property hash.
 *
 * @see {@link https://github.com/rtfeldman/seamless-immutable}
 *
 * @class
 * @abstract
 */


export default class ImmutableModel {
  /**
   * Will create an immutable instance object composed of the passed properties.
   *
   * @constructor
   * @param {Object}  props                 Object hash of properties. Only enumerable own props will be evaluated. Required.
   * @throws {TypeError} Initial properties hash is required.
   */
  constructor(props) {
    if (props !== Object(props)) {
      throw new TypeError(`Initial properties hash is required, received '${props}'`);
    }

    const DEFAULT_FIELDS = result(this.constructor, 'DEFAULT_FIELDS');

    const __props = DEFAULT_FIELDS ? Object.assign({}, DEFAULT_FIELDS, {}, props) : props;

    const {
      REQUIRED_FIELDS
    } = this.constructor;

    if (REQUIRED_FIELDS) {
      REQUIRED_FIELDS.forEach(field => {
        if (__props[field] !== undefined) {
          return;
        }

        const ctorName = tryGetConstructorName(this.constructor, 'ImmutableModel');
        throw new Error(`Field '${field}' is missing and is a required field of model '${ctorName}'`);
      });
    }

    Object.keys(__props).forEach(key => {
      this[key] = __props[key];
    });
    return SeamlessImmutable(this, {
      prototype: Object.getPrototypeOf(this)
    });
  }

}
/**
 * Default fields to be added to model if missing from `props` passed to constructor.
 *
 * @virtual
 * @constant
 * @static
 * @type {Object|Function} Object of key/value pairs, or a function that returns such an object.
 */

ImmutableModel.DEFAULT_FIELDS = null;
/**
 * TODO: Add optional type validators?
 * Required fields that must be present in props passed to constructor.
 *
 * @virtual
 * @constant
 * @static
 * @type {Array}
 */

ImmutableModel.REQUIRED_FIELDS = null;